// Helper functions for badge types

// Industry badge type mapping
const getIndustryBadgeType = (industry) => {
  if (!industry) return 'default';
  
  const industryLower = industry.toLowerCase();
  
  if (industryLower.includes('tech') || industryLower.includes('software') || industryLower.includes('it')) {
    return 'tech';
  } else if (industryLower.includes('finance') || industryLower.includes('bank') || industryLower.includes('investment')) {
    return 'finance';
  } else if (industryLower.includes('health') || industryLower.includes('medical') || industryLower.includes('pharma')) {
    return 'healthcare';
  } else if (industryLower.includes('education') || industryLower.includes('school') || industryLower.includes('university')) {
    return 'education';
  } else if (industryLower.includes('retail') || industryLower.includes('ecommerce') || industryLower.includes('commerce')) {
    return 'retail';
  } else {
    return 'default';
  }
};

// Company size badge type mapping
const getCompanySizeBadgeType = (employeeRange) => {
  if (!employeeRange) return 'startup';
  
  const range = employeeRange.toLowerCase();
  
  if (range.includes('1-10') || range.includes('1 to 10')) {
    return 'startup';
  } else if (range.includes('11-50') || range.includes('11 to 50')) {
    return 'small';
  } else if (range.includes('51-200') || range.includes('51 to 200')) {
    return 'medium';
  } else if (range.includes('201-1000') || range.includes('201 to 1000')) {
    return 'large';
  } else if (range.includes('1000+') || range.includes('1001+')) {
    return 'enterprise';
  } else {
    return 'startup';
  }
};

// Jobs count badge type mapping
const getJobsCountBadgeType = (jobsCount) => {
  const count = parseInt(jobsCount) || 0;
  
  if (count === 0) {
    return 'none';
  } else if (count >= 1 && count <= 5) {
    return 'low';
  } else if (count >= 6 && count <= 15) {
    return 'medium';
  } else {
    return 'high';
  }
};

// Alternative: More detailed jobs count function
const getDetailedJobsCountBadge = (jobsCount) => {
  const count = parseInt(jobsCount) || 0;
  
  if (count === 0) {
    return { type: 'none', text: 'No Jobs', icon: '❌' };
  } else if (count >= 1 && count <= 2) {
    return { type: 'low', text: `${count} Job${count > 1 ? 's' : ''}`, icon: '📄' };
  } else if (count >= 3 && count <= 10) {
    return { type: 'medium', text: `${count} Jobs`, icon: '📋' };
  } else {
    return { type: 'high', text: `${count} Jobs`, icon: '🔥' };
  }
};

// Status badge for company activity
const getCompanyStatusBadge = (totalJobs, activeJobs, createdAt) => {
  const total = parseInt(totalJobs) || 0;
  const active = parseInt(activeJobs) || 0;
  const daysSinceCreated = Math.floor((new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24));
  
  if (total === 0) {
    return 'inactive';
  } else if (active > 0 && active >= total * 0.5) {
    return 'highly-active';
  } else if (active > 0) {
    return 'moderately-active';
  } else if (daysSinceCreated < 30) {
    return 'new';
  } else {
    return 'dormant';
  }
};

// Industry color mapping for more variety
const getIndustryColor = (industry) => {
  const colors = {
    'technology': '#3b82f6',
    'finance': '#10b981',
    'healthcare': '#ef4444',
    'education': '#8b5cf6',
    'retail': '#f59e0b',
    'manufacturing': '#6b7280',
    'consulting': '#06b6d4',
    'media': '#ec4899',
    'real-estate': '#84cc16',
    'default': '#6b7280'
  };
  
  return colors[getIndustryBadgeType(industry)] || colors.default;
};

// Format employee range for display
const formatEmployeeRange = (range) => {
  if (!range) return 'Not specified';
  
  // Convert ranges like "1-10" to "1-10 employees"
  if (range.includes('-') || range.includes('+')) {
    return `${range} employees`;
  }
  
  return range;
};

// Company age calculator
const getCompanyAge = (createdAt) => {
  const created = new Date(createdAt);
  const now = new Date();
  const diffTime = Math.abs(now - created);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 30) {
    return `${diffDays} days old`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} old`;
  } else {
    const years = Math.floor(diffDays / 365);
    return `${years} year${years > 1 ? 's' : ''} old`;
  }
};

// Export all functions for use in your component
export {
  getIndustryBadgeType,
  getCompanySizeBadgeType,
  getJobsCountBadgeType,
  getDetailedJobsCountBadge,
  getCompanyStatusBadge,
  getIndustryColor,
  formatEmployeeRange,
  getCompanyAge
};